import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { FORM } from '@/constants/testIds';
import { Mail, Calendar, Sparkles, Clock } from 'lucide-react';
import LeadMagnetsBlock from '@/components/LeadMagnetsBlock';
import CalEmbed from '@/components/CalEmbed';
import { FunnelBlock } from '@/components/Blocks';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  'Cleaning',
  'Home Services',
  'Wellness',
  'Coaching',
  'Agency',
  'Professional Services',
  'Other',
];

const SERVICE_OPTIONS = [
  'Virtual Support',
  'Website Design',
  'SEO Optimization',
  'Social Media Management',
  'Operations Consulting',
];

export default function Contact() {
  const [tab, setTab] = useState('discovery');
  useSEO({
    title: 'Contact — Book a Discovery Call',
    description:
      'Book a discovery call or request a consultation. A short, focused conversation about your business — no pressure, no pitch. Typical response within one business day.',
    path: '/contact',
  });

  return (
    <div>
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 max-w-4xl">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[80px] leading-[1] tracking-tight mt-6">
            Let&rsquo;s build better systems<br/><span className="font-serif-italic italic text-[#C9A24D]">together.</span>
          </h1>
          <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed">
            Whether you are looking for operational support, a new website, improved visibility through SEO, or help building dependable systems, I would love to learn more about your business and how I can help.
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <aside className="lg:col-span-4 space-y-10">
            <div className="card-soft p-8">
              <Calendar size={26} strokeWidth={1.25} className="text-[#C9A24D]" />
              <h3 className="font-serif-display text-2xl mt-5">Discovery Call</h3>
              <p className="text-[#1C1C1C]/75 leading-relaxed mt-3 text-sm">
                Pick a 30-minute slot that works for you. A short, focused conversation about your business — no pressure, no pitch.
              </p>
            </div>
            <div className="card-soft p-8">
              <Mail size={26} strokeWidth={1.25} className="text-[#C9A24D]" />
              <h3 className="font-serif-display text-2xl mt-5">Direct Email</h3>
              <a href="mailto:imhallie09@gmail.com" data-testid="contact-email" className="text-[#1C1C1C] gold-underline mt-3 inline-block text-sm tracking-wide">
                imhallie09@gmail.com
              </a>
              <a href="https://wa.me/639638096014" target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="block text-[#1C1C1C] gold-underline mt-3 text-sm tracking-wide">
                WhatsApp +63 963 809 6014
              </a>
            </div>
            <div className="card-soft p-8">
              <Clock size={26} strokeWidth={1.25} className="text-[#C9A24D]" />
              <h3 className="font-serif-display text-2xl mt-5">Response Time</h3>
              <p className="text-[#1C1C1C]/75 leading-relaxed mt-3 text-sm">
                Typical reply within one business day. Thoughtful, written, and to the point.
              </p>
            </div>
            <div className="card-soft p-8">
              <Sparkles size={26} strokeWidth={1.25} className="text-[#C9A24D]" />
              <h3 className="font-serif-display text-2xl mt-5">A note on pricing</h3>
              <p className="text-[#1C1C1C]/75 leading-relaxed mt-3 text-sm">
                Engagements are scoped to your business. Pricing is shared in writing once we have understood the work together.
              </p>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <div className="flex gap-px bg-[#E6CFC7]/60 mb-10">
              <button
                onClick={() => setTab('discovery')}
                data-testid="contact-tab-discovery"
                className={`flex-1 py-4 text-xs uppercase tracking-[0.18em] transition-colors ${tab === 'discovery' ? 'bg-[#1C1C1C] text-[#F4EFEA]' : 'bg-[#F4EFEA] text-[#1C1C1C] hover:text-[#C9A24D]'}`}
              >
                Book a Discovery Call
              </button>
              <button
                onClick={() => setTab('general')}
                data-testid="contact-tab-general"
                className={`flex-1 py-4 text-xs uppercase tracking-[0.18em] transition-colors ${tab === 'general' ? 'bg-[#1C1C1C] text-[#F4EFEA]' : 'bg-[#F4EFEA] text-[#1C1C1C] hover:text-[#C9A24D]'}`}
              >
                Request a Consultation
              </button>
            </div>

            {tab === 'discovery' ? <CalEmbed /> : <GeneralForm />}
          </div>
        </div>
      </section>

      <FunnelBlock />
      <LeadMagnetsBlock />
    </div>
  );
}

// (Legacy DiscoveryForm retained below — Cal.com embed is the primary booking
// experience. The component is no longer rendered but kept for reference.)

function DiscoveryForm() {
  const [data, setData] = useState({ name: '', email: '', business_name: '', preferred_date: '', timezone_str: '', biggest_challenge: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/discovery-call`, data);
      toast.success('Request received. I will reply within one business day.');
      setData({ name: '', email: '', business_name: '', preferred_date: '', timezone_str: '', biggest_challenge: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={FORM.discoveryForm} className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <Field label="Your name">
          <input data-testid={FORM.discoveryName} required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Jane Founder" className="input-editorial" />
        </Field>
        <Field label="Email">
          <input data-testid={FORM.discoveryEmail} type="email" required value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} placeholder="jane@business.com" className="input-editorial" />
        </Field>
        <Field label="Business name">
          <input data-testid={FORM.discoveryBusiness} value={data.business_name} onChange={(e) => setData({ ...data, business_name: e.target.value })} placeholder="Your business" className="input-editorial" />
        </Field>
        <Field label="Preferred date / time">
          <input data-testid={FORM.discoveryDate} value={data.preferred_date} onChange={(e) => setData({ ...data, preferred_date: e.target.value })} placeholder="e.g. Tuesday morning" className="input-editorial" />
        </Field>
        <Field label="Your timezone">
          <input value={data.timezone_str} onChange={(e) => setData({ ...data, timezone_str: e.target.value })} placeholder="e.g. EST / GMT" className="input-editorial" />
        </Field>
      </div>
      <Field label="What is the biggest challenge in your business right now?">
        <textarea
          data-testid={FORM.discoveryChallenge}
          required
          rows={4}
          value={data.biggest_challenge}
          onChange={(e) => setData({ ...data, biggest_challenge: e.target.value })}
          placeholder="Tell me a little about where things are stuck\u2026"
          className="input-editorial resize-none"
        />
      </Field>
      <button type="submit" disabled={loading} data-testid={FORM.discoverySubmit} className="btn-primary">
        {loading ? 'Sending\u2026' : 'Request Discovery Call'}
      </button>
    </form>
  );
}

function GeneralForm() {
  const [data, setData] = useState({ name: '', email: '', business_name: '', business_type: '', services_interest: [], message: '' });
  const [loading, setLoading] = useState(false);

  const toggle = (s) => {
    setData((d) => ({
      ...d,
      services_interest: d.services_interest.includes(s) ? d.services_interest.filter((x) => x !== s) : [...d.services_interest, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...data, source: 'contact_form' });
      toast.success('Thank you. I will be in touch shortly.');
      setData({ name: '', email: '', business_name: '', business_type: '', services_interest: [], message: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={FORM.contactForm} className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <Field label="Your name">
          <input data-testid={FORM.contactName} required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="input-editorial" placeholder="Jane Founder" />
        </Field>
        <Field label="Email">
          <input data-testid={FORM.contactEmail} type="email" required value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="input-editorial" placeholder="jane@business.com" />
        </Field>
        <Field label="Business name">
          <input data-testid={FORM.contactBusiness} value={data.business_name} onChange={(e) => setData({ ...data, business_name: e.target.value })} className="input-editorial" />
        </Field>
        <Field label="Business type">
          <select data-testid={FORM.contactBusinessType} value={data.business_type} onChange={(e) => setData({ ...data, business_type: e.target.value })} className="input-editorial">
            <option value="">Select\u2026</option>
            {BUSINESS_TYPES.map((b) => <option key={b}>{b}</option>)}
          </select>
        </Field>
      </div>
      <Field label="What are you interested in?">
        <div className="flex flex-wrap gap-2">
          {SERVICE_OPTIONS.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => toggle(s)}
              className={`text-xs uppercase tracking-[0.14em] px-4 py-2 border transition-colors ${
                data.services_interest.includes(s)
                  ? 'bg-[#1C1C1C] text-[#F4EFEA] border-[#1C1C1C]'
                  : 'border-[#E6CFC7] text-[#1C1C1C] hover:border-[#C9A24D] hover:text-[#C9A24D]'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Your message">
        <textarea
          data-testid={FORM.contactMessage}
          required
          rows={5}
          value={data.message}
          onChange={(e) => setData({ ...data, message: e.target.value })}
          className="input-editorial resize-none"
          placeholder="Tell me a little about your business and what you need\u2026"
        />
      </Field>
      <button type="submit" disabled={loading} data-testid={FORM.contactSubmit} className="btn-primary">
        {loading ? 'Sending\u2026' : 'Send Message'}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-[#1C1C1C]/70">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
